/**
 * SPRINT 2 — ComplementaryMedsList
 * ================================
 * Gère la liste des médicaments complémentaires (bloc B du plan).
 * Props:
 *   items   : array<ComplementaryMed>
 *   onChange: (newItems) => void
 *   catalog : array<ReferenceProtocol> (kind=COMPLEMENTARY, pour autocomplete)
 */
import { useState } from "react";
import { Plus, X } from "lucide-react";

const CATEGORIES = [
  { value: "ANTIEMETIC", label: "Anti-émétique" },
  { value: "CORTICOID", label: "Corticoïde" },
  { value: "GCSF", label: "G-CSF" },
  { value: "EPO", label: "EPO" },
  { value: "BISPHOSPHONATE", label: "Bisphosphonate" },
  { value: "DECAPEPTYL", label: "Decapeptyl" },
  { value: "HYDRATATION", label: "Hydratation" },
  { value: "OTHER", label: "Autre" },
];

const ROUTES = ["IV", "PO", "SC", "IM", "Intrathecale"];

export default function ComplementaryMedsList({ items = [], onChange, catalog = [] }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ category: "ANTIEMETIC", dci: "", doseFormula: "", route: "IV", timing: "" });

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const add = () => {
    if (!draft.dci?.trim()) return;
    onChange([...items, { ...draft, dci: draft.dci.trim() }]);
    setDraft({ category: "ANTIEMETIC", dci: "", doseFormula: "", route: "IV", timing: "" });
    setAdding(false);
  };

  const catalogForCategory = (cat) => catalog.filter((c) => c.category === cat);

  const pickFromCatalog = (ref) => {
    const first = ref.components?.[0] || {};
    setDraft({
      category: ref.category || "OTHER",
      dci: first.dci || ref.label,
      doseFormula: first.doseFormula || "",
      route: first.route || "IV",
      timing: first.days || "",
      referenceCode: ref.code,
    });
  };

  return (
    <div className="space-y-2" data-testid="complementary-meds-list">
      {items.length === 0 && !adding && (
        <p className="text-xs text-slate-400">Aucun complémentaire. Cliquez pour ajouter.</p>
      )}

      {items.map((m, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          data-testid={`complementary-item-${i}`}
        >
          <div className="flex flex-wrap items-baseline gap-x-2 min-w-0">
            <span className="inline-block text-[10px] uppercase tracking-wide text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
              {CATEGORIES.find((c) => c.value === m.category)?.label || m.category}
            </span>
            <span className="font-medium text-slate-800 truncate">{m.dci}</span>
            {m.doseFormula && <span className="text-slate-500 text-xs">{m.doseFormula}</span>}
            {m.route && <span className="text-slate-400 text-xs">{m.route}</span>}
            {m.timing && <span className="text-slate-400 text-xs">· {m.timing}</span>}
          </div>
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-slate-400 hover:text-red-600 flex-shrink-0"
            data-testid={`complementary-remove-${i}`}
            aria-label="Retirer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      {adding ? (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid="complementary-add-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">Catégorie</label>
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="complementary-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Du catalogue</label>
              <select
                onChange={(e) => {
                  const ref = catalogForCategory(draft.category).find((r) => r.code === e.target.value);
                  if (ref) pickFromCatalog(ref);
                }}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="complementary-catalog-select"
                defaultValue=""
              >
                <option value="">— Sélectionner —</option>
                {catalogForCategory(draft.category).map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">DCI / Médicament *</label>
            <input
              type="text"
              value={draft.dci}
              onChange={(e) => setDraft({ ...draft, dci: e.target.value })}
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
              placeholder="ex. Ondansétron"
              data-testid="complementary-dci-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-slate-600">Dose</label>
              <input
                type="text"
                value={draft.doseFormula}
                onChange={(e) => setDraft({ ...draft, doseFormula: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                placeholder="ex. 8 mg"
                data-testid="complementary-dose-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Voie</label>
              <select
                value={draft.route}
                onChange={(e) => setDraft({ ...draft, route: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="complementary-route-select"
              >
                {ROUTES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Timing</label>
              <input
                type="text"
                value={draft.timing}
                onChange={(e) => setDraft({ ...draft, timing: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                placeholder="H-30min"
                data-testid="complementary-timing-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setAdding(false); setDraft({ category: "ANTIEMETIC", dci: "", doseFormula: "", route: "IV", timing: "" }); }}
              className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1"
              data-testid="complementary-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={add}
              disabled={!draft.dci?.trim()}
              className="text-xs bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 disabled:opacity-40"
              data-testid="complementary-add-confirm-btn"
            >
              Ajouter
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full text-sm text-blue-600 hover:text-blue-800 border border-dashed border-slate-300 rounded py-2 flex items-center justify-center gap-1"
          data-testid="complementary-add-btn"
        >
          <Plus className="w-4 h-4" /> Ajouter un médicament complémentaire
        </button>
      )}
    </div>
  );
}
